import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type InboxItem } from '../api';
import { Loading, Empty, ErrorState, Due, StageBar } from '../components/ui';
import { meta } from '../module';

/**
 * The screen that decides whether anyone opens this system tomorrow.
 *
 * The prototype at pm.ragana.tech had no equivalent — its users had
 * to go looking for the documents waiting on them. Sorted by due
 * date, overdue first, because that is the order the work is actually
 * urgent in.
 */
export function Inbox({ onCountChange }: { onCountChange: (n: number) => void }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await api.get<{ items: InboxItem[] }>('/inbox');
      setItems(res.items);
      onCountChange(res.items.length);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your inbox.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overdue = items?.filter((i) => i.overdue) ?? [];
  const rest = items?.filter((i) => !i.overdue) ?? [];

  function Row({ i }: { i: InboxItem }) {
    return (
      <tr
        onClick={() => navigate(`/documents/${i.document.id}`)}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate(`/documents/${i.document.id}`)}
      >
        <td className="num">{i.document.docNumber}</td>
        <td>{i.document.title}</td>
        <td>
          L{i.level} · {i.role}
          {i.discipline ? ` · ${i.discipline.code}` : ''}
        </td>
        <td>
          <StageBar stage={i.revision.stage} />
        </td>
        <td className="num">Rev {i.revision.revCode}</td>
        <td>
          <Due dueAt={i.dueAt} />
        </td>
      </tr>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inbox</h1>
          <p>
            {items === null
              ? 'Loading…'
              : items.length === 0
                ? 'Nothing waiting on you'
                : `${items.length} waiting on you${overdue.length ? `, ${overdue.length} overdue` : ''}`}
          </p>
        </div>
      </div>

      {error && <ErrorState message={error} onRetry={load} />}
      {!error && items === null && <Loading />}

      {!error && items !== null && items.length === 0 && (
        <Empty
          title="You are clear"
          body="Nothing is waiting on your review right now."
          action={
            <button className="btn btn-ghost" onClick={() => navigate('/')}>
              Open the {meta.noun} register
            </button>
          }
        />
      )}

      {!error && items !== null && items.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Number</th>
                <th>Title</th>
                <th>Your step</th>
                <th>Stage</th>
                <th>Rev</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {/* Overdue first, always — not sorted alphabetically into
                  the middle of the list where it reads as routine. */}
              {overdue.map((i) => (
                <Row key={i.stepId} i={i} />
              ))}
              {rest.map((i) => (
                <Row key={i.stepId} i={i} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
